/*
 * Assigning the tags an article can carry any number of - which, since the
 * product became a NOT NULL column on kb.Article, means the categories and
 * nothing else. The product is chosen by ProductSelect, which wears this
 * control's clothes over a radio group.
 *
 * `kind` stays even though only one value is passed today. This is a control
 * over a tag CATALOGUE, not a category control: `kind` is how it names the
 * catalogue in its own empty state, and hard-coding the word would turn a
 * general control into a specific one in everything but its name. What it must
 * not be given again is 'product' - that is a single choice now, and the
 * arity is the difference the two controls exist to express.
 *
 * "One or more" is enforced here as well as on the server, and the reason it has to
 * be enforced at all is in the data model: SQL Server has no deferred constraints,
 * so kb.Article must be insertable for the instant before its first kb.ArticleCategory
 * row exists. The rule therefore lives in the application rather than in a constraint.
 * (The product needs no such rule: a NOT NULL column with a foreign key IS the
 * constraint, which is exactly why it stopped being a junction.)
 *
 * The form announces it with the asterisk on the label and, when it is broken,
 * with the error under the box - not with a standing sentence under every picker.
 * Both pages that use this component show it beside the product picker, so that
 * sentence appeared twice on one screen to say what the asterisk already says.
 *
 * Inactive tags are shown only when already assigned. A retired tag must stay
 * visible on the articles that carry it - dropping it from the list would mean saving
 * an article silently untagged it - but it is not offered for new assignments.
 *
 * The box of checkboxes scrolls at a fixed height, EXCEPT when `grow` is set: it
 * then takes whatever height its card has spare, with that fixed height as a floor
 * rather than a ceiling. Only one picker in a card can usefully do that, which is
 * why it is a prop and not the default - see .field-grow in the stylesheet.
 *
 * Its words come from the chosen language's dictionary, `t.admin.articles` (S48);
 * the `label` is the caller's.
 */

import { useMessages } from '../../i18n/language.js';

export interface TagOption {
  id: number;
  name: string;
  isActive: boolean;
}

export function TagMultiSelect({
  label,
  options,
  selected,
  onChange,
  kind,
  error,
  grow = false,
}: {
  label: string;
  options: TagOption[];
  selected: number[];
  onChange: (next: number[]) => void;
  kind: 'product' | 'category';
  error?: string | undefined;
  /**
   * Take the spare height of the surrounding card instead of scrolling at a fixed
   * height. Requires that card to be a flex column - .properties-card is.
   */
  grow?: boolean;
}): JSX.Element {
  const words = useMessages().admin.articles.tags;
  const visible = options.filter((option) => option.isActive || selected.includes(option.id));

  const toggle = (id: number) =>
    onChange(selected.includes(id) ? selected.filter((value) => value !== id) : [...selected, id]);

  return (
    <div
      className={`field${error ? ' field-invalid' : ''}${grow ? ' field-grow' : ''}`}
    >
      <label>
        {label}
        <span aria-hidden="true"> *</span>
      </label>

      {/* A class rather than inline styles, because .field-grow has to be able to
          override the height - which a rule cannot do to a style attribute. */}
      <div className="tag-picker">
        {visible.length === 0 ? (
          <p className="muted" style={{ margin: 0, fontSize: '0.875rem' }}>
            {words.noTags(kind)}
          </p>
        ) : (
          <ul className="checkbox-list">
            {visible.map((option) => (
              <li key={option.id}>
                <label>
                  <input
                    type="checkbox"
                    checked={selected.includes(option.id)}
                    onChange={() => toggle(option.id)}
                  />
                  <span>
                    {option.name}
                    {!option.isActive && <span className="faint"> {words.retired}</span>}
                  </span>
                </label>
              </li>
            ))}
          </ul>
        )}
      </div>

      {error && (
        <p className="field-error" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
